import React, {useRef, useState} from "react";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {flex, mt, onDesktop, p, ph, prettyScroll} from "@pg-design/helpers-css";
import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {MapMarkerOutlineIcon} from "@pg-design/icons-module";
import {SystemModal} from "@pg-design/modal-module";
import {PaginationSize, PaginationWithList} from "@pg-design/pagination";
import {Switcher} from "@pg-design/switcher-module";
import {Text} from "@pg-design/text-module";
import {QueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty} from "@pg-mono/nodash";

import {ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {useResponsiveLinkTarget} from "../../../hooks/use_responsive_link_target";
import {GoToComparisonToolButton} from "../../../my_offers/components/GoToComparisonToolButton";
import {usePropertyList} from "../../../property_list/hooks/use_property_list";
import {PropertyListTable} from "../../../property_list/PropertyListTable";
import {PropertyListTabSwitcher, PropertyListType} from "../../../property_list/PropertyListTabSwitcher";
import {PropertyListTileList} from "../../../property_list/PropertyListTileList";
import {Country} from "../../../region/types/Country";
import {fadeInAnimation} from "../../../search/components/atoms/atoms";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {PropertyListTabTypes} from "../../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../../detail/components/property_list/OfferDetailPropertyListTable";
import {useOfferLink} from "../../detail/hooks/use_offer_link";
import {IOfferGroupsSimplified} from "../../detail/types/IOfferGroups";
import {OfferDisplayType} from "../../helpers/OfferDisplayType";
import {getOfferTypeNameCapital} from "../../helpers/OfferType";
import {IOfferDetail} from "../../types/IOfferDetail";
import {ISalesOffice} from "../../types/ISalesOffice";
import {OfferPriceType} from "../../types/OfferPriceType";
import {OfferType} from "../../types/OfferType";

interface IProps {
    onModalClose?: (e: React.MouseEvent | React.KeyboardEvent) => void;
    modalState: boolean;
    offer: IModalPropertyListOffer;
    sourceSection?: ApplicationSourceSection;
    latestSearchFilters: QueryValues;
    propertyListType?: PropertyListType;
}

export interface IModalPropertyListOffer {
    address: string;
    configuration: {
        display_type: OfferDisplayType;
        hide_properties_prices_for_anonymous_users: boolean;
        is_autoresponder_enabled: boolean;
        limited_presentation?: boolean;
        phone_clip: string | null;
        personal_data_processor_name: string | null;
        personal_data_processor_url: string | null;
        privacy_policy_url: string | null;
    };
    has_active_promotions: boolean;
    has_required_prices: boolean | null;
    geo_point: {
        coordinates: [number, number];
    };
    id: number;
    is_active?: boolean;
    is_holiday_location: boolean;
    name: string;
    is_phone_clip_enabled: boolean;
    region: {
        country: Country;
        id: number;
        full_name: string;
    };
    sales_offices: ISalesOffice[];
    stats: {
        properties_with_roi_count: number;
        properties_count_for_sale: number;
    } | null;
    price_type?: OfferPriceType | null;
    slug: string;
    type: OfferType;
    vendor: {
        configuration: {
            privacy_policy_url?: string | null;
            privacy_policy_file?: string | null;
        };
        id: number;
        logo: {
            v_log_80x60: string;
            v_log_120x90: string;
            v_log_160x120: string;
        } | null;
        name: string;
        offices?: {
            address: string;
            phone: string;
            mobile: string;
            type: number;
            region: {
                id: number;
                short_name: string;
            };
        }[];
        slug: string;
    };
    groups: IOfferGroupsSimplified | null;
}

export const DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE = 20;

export const OfferListPropertiesModal: React.FC<IProps> = (props) => {
    const pageChangeScrollToRef = useRef<HTMLDivElement>(null);
    const propertyList = usePropertyList(props.offer.id, PropertyListTabTypes.MODAL, pageChangeScrollToRef, DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE);
    const [currentPropertyListType, setCurrentPropertyListType] = useState(props.propertyListType ?? PropertyListType.TABLE);
    const [comparisonMode, setComparisonMode] = useState(false);

    const theme = useTheme();

    const {properties, pageCount, onPageChange: onPropertyListPageChange, onSortChange, sort, page, requestState, pageIsLoading} = propertyList;

    const onModalClose = (e: React.MouseEvent | React.KeyboardEvent) => {
        props.onModalClose && props.onModalClose(e);
        page.setCurrentPage(1);
    };

    const onChangePropertyListType = (listType: PropertyListType) => {
        setCurrentPropertyListType(listType);
        listType === PropertyListType.TILES && showPropertyPlanView(ShowPropertyPlanView.PLAN_DEFAULT, props.offer, props.offer.vendor);
    };

    const offerLink = useOfferLink(props.offer);
    const target = useResponsiveLinkTarget();

    const propertyListFilters = isEmpty(props.latestSearchFilters) ? {} : toQueryValues(propertyListTableFiltersFields, props.latestSearchFilters);

    const onPageChange = (page: number) => {
        return onPropertyListPageChange(page, propertyListFilters);
    };

    return (
        <PropertyListModal onModalClose={onModalClose} isOpen={props.modalState}>
            <div css={innerWrap}>
                {props.offer && (
                    <div css={headerStyles}>
                        {props.offer.vendor.logo && (
                            <div css={logoWrapperStyles}>
                                <img css={logoImage} alt={`${props.offer.vendor.name} logo`} src={props.offer.vendor.logo.v_log_120x90} />
                            </div>
                        )}

                        <div>
                            <Text as="h2" variant="headline_3">
                                <a href={offerLink} target={target}>
                                    {props.offer.name}
                                </a>
                            </Text>

                            {props.offer.vendor && (
                                <Text color={theme.colors.gray[700]} variant="body_copy_2" className="bt">
                                    {props.offer.vendor.name}
                                </Text>
                            )}

                            <div css={[flex("center"), mt()]}>
                                <div css={markerIconWrap}>
                                    <MapMarkerOutlineIcon size="2" />
                                </div>

                                {props.offer.type && props.offer.address && (
                                    <Text color={theme.colors.gray[700]} as="address" variant="info_txt_1">
                                        {getOfferTypeNameCapital(props.offer.type)} <span className="bt">{props.offer.address}</span>
                                    </Text>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                <>
                    {props.offer && (
                        <PropertyListTableHolder>
                            <span ref={pageChangeScrollToRef} />

                            <div css={propertyListSwitchersWrapStyle}>
                                <PropertyListTabSwitcher
                                    currentPropertyListType={currentPropertyListType}
                                    setCurrentPropertyListType={onChangePropertyListType}
                                />
                                <div css={[switcherHolder]}>
                                    <Switcher
                                        id="comparison-switcher"
                                        labelContent="Porównaj"
                                        onChange={() => {
                                            setComparisonMode(!comparisonMode);
                                        }}
                                        variant="big"
                                        checked={comparisonMode}
                                        value="comparison-mode"
                                    />
                                </div>
                            </div>

                            {currentPropertyListType === PropertyListType.TABLE && (
                                <div css={propertyListTableWrapper}>
                                    <PropertyListTable
                                        requestState={requestState}
                                        offer={props.offer}
                                        properties={properties}
                                        sort={sort.currentSort}
                                        onSortChange={(value) => onSortChange(value, propertyListFilters)}
                                        onPageChange={onPageChange}
                                        pageCount={pageCount}
                                        pageIsLoading={pageIsLoading}
                                        currentPage={page.currentPage}
                                        sourceSection={props.sourceSection}
                                        css={[modalPropertyList]}
                                        comparisonMode={comparisonMode}
                                    />
                                </div>
                            )}

                            {currentPropertyListType === PropertyListType.TILES && (
                                <PropertyListTileList
                                    requestState={requestState}
                                    properties={properties}
                                    offer={props.offer as IOfferDetail}
                                    css={[modalPropertyList]}
                                    comparisonMode={comparisonMode}
                                    gridLayoutCols={{md: 2, lg: 2}}
                                />
                            )}
                        </PropertyListTableHolder>
                    )}
                    {comparisonMode && (
                        <div css={comparisonToolLinkWrap(currentPropertyListType === PropertyListType.TILES)}>
                            <GoToComparisonToolButton />
                        </div>
                    )}

                    {pageCount > 0 && (
                        <div css={paginationHolder}>
                            <PaginationWithList
                                iconPrev={<ChevronLeftIcon />}
                                iconNext={<ChevronRightIcon />}
                                onChangePageClick={onPageChange}
                                requestState={requestState}
                                pageCount={pageCount}
                                currentPage={page.currentPage}
                                multiNumbersInside
                                size={PaginationSize.SM}
                            />
                        </div>
                    )}
                </>
            </div>
        </PropertyListModal>
    );
};

const PropertyListModal = styled(SystemModal)`
    ${fadeInAnimation("0.2s")};
`;

const innerWrap = (theme: Theme) => css`
    position: relative;
    max-height: 100vh;
    height: 100%;
    width: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    @media (min-width: ${theme.breakpoints.sm}) {
        height: 630px;
        width: 810px;
        padding: 0;
    }
`;

const PropertyListTableHolder = styled.div`
    flex-grow: 1;
    flex-shrink: 1;
    overflow-x: hidden;
    ${prettyScroll()};
`;

const modalPropertyList = css`
    margin-bottom: 0 !important;
`;

const headerStyles = (theme: Theme) => css`
    ${p(2, 0, 4, 2)};
    padding-right: 3.8rem;
    display: flex;
    flex-shrink: 0;

    @media (min-width: ${theme.breakpoints.xs}) {
        padding: 2.2rem;
    }
`;

const propertyListTableWrapper = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.md}) {
        ${ph(2)};
    }
`;

const markerIconWrap = css`
    margin-right: 1.2rem;
`;

const logoWrapperStyles = css`
    margin-right: 1.2rem;
`;

const logoImage = (theme: Theme) => css`
    max-width: 8.6rem;
    max-height: 6.4rem;

    @media (min-width: ${theme.breakpoints.xs}) {
        max-width: 11.2rem;
        max-height: 8.4rem;
    }
`;

const paginationHolder = (theme: Theme) => css`
    display: flex;
    justify-content: center;
    background: ${theme.colors.gray[100]};
    width: 100%;
    padding: 1rem;
    flex-shrink: 0;

    @media (min-width: ${theme.breakpoints.sm}) {
        position: sticky;
        bottom: 0;
        padding: 0.3rem;
    }
`;

const propertyListSwitchersWrapStyle = css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    ${ph(2)}

    ${onDesktop(css`
        flex-direction: row;
    `)}
`;

const comparisonToolLinkWrap = (inTiles: boolean) => () => css`
    ${p(1.5)}
    display: flex;
    align-items: center;
    justify-content: ${inTiles ? "center" : "flex-end"};
`;

const switcherHolder = css`
    align-self: flex-end;

    ${onDesktop(css`
        align-self: center;
    `)}
`;
import React, {useRef, useState} from "react";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {flex, mt, onDesktop, p, ph, prettyScroll} from "@pg-design/helpers-css";
import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {MapMarkerOutlineIcon} from "@pg-design/icons-module";
import {SystemModal} from "@pg-design/modal-module";
import {PaginationSize, PaginationWithList} from "@pg-design/pagination";
import {Switcher} from "@pg-design/switcher-module";
import {Text} from "@pg-design/text-module";
import {QueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty} from "@pg-mono/nodash";

import {ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {useResponsiveLinkTarget} from "../../../hooks/use_responsive_link_target";
import {GoToComparisonToolButton} from "../../../my_offers/components/GoToComparisonToolButton";
import {usePropertyList} from "../../../property_list/hooks/use_property_list";
import {PropertyListTable} from "../../../property_list/PropertyListTable";
import {PropertyListTabSwitcher, PropertyListType} from "../../../property_list/PropertyListTabSwitcher";
import {PropertyListTileList} from "../../../property_list/PropertyListTileList";
import {Country} from "../../../region/types/Country";
import {fadeInAnimation} from "../../../search/components/atoms/atoms";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {PropertyListTabTypes} from "../../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../../detail/components/property_list/OfferDetailPropertyListTable";
import {useOfferLink} from "../../detail/hooks/use_offer_link";
import {IOfferGroupsSimplified} from "../../detail/types/IOfferGroups";
import {OfferDisplayType} from "../../helpers/OfferDisplayType";
import {getOfferTypeNameCapital} from "../../helpers/OfferType";
import {IOfferDetail} from "../../types/IOfferDetail";
import {ISalesOffice} from "../../types/ISalesOffice";
import {OfferPriceType} from "../../types/OfferPriceType";
import {OfferType} from "../../types/OfferType";

interface IProps {
    onModalClose?: (e: React.MouseEvent | React.KeyboardEvent) => void;
    modalState: boolean;
    offer: IModalPropertyListOffer;
    sourceSection?: ApplicationSourceSection;
    latestSearchFilters: QueryValues;
    propertyListType?: PropertyListType;
}

export interface IModalPropertyListOffer {
    address: string;
    configuration: {
        display_type: OfferDisplayType;
        hide_properties_prices_for_anonymous_users: boolean;
        is_autoresponder_enabled: boolean;
        limited_presentation?: boolean;
        phone_clip: string | null;
        personal_data_processor_name: string | null;
        personal_data_processor_url: string | null;
        privacy_policy_url: string | null;
    };
    has_active_promotions: boolean;
    has_required_prices: boolean | null;
    geo_point: {
        coordinates: [number, number];
    };
    id: number;
    is_active?: boolean;
    is_holiday_location: boolean;
    name: string;
    is_phone_clip_enabled: boolean;
    region: {
        country: Country;
        id: number;
        full_name: string;
    };
    sales_offices: ISalesOffice[];
    stats: {
        properties_with_roi_count: number;
        properties_count_for_sale: number;
    } | null;
    price_type?: OfferPriceType | null;
    slug: string;
    type: OfferType;
    vendor: {
        configuration: {
            privacy_policy_url?: string | null;
            privacy_policy_file?: string | null;
        };
        id: number;
        logo: {
            v_log_80x60: string;
            v_log_120x90: string;
            v_log_160x120: string;
        } | null;
        name: string;
        offices?: {
            address: string;
            phone: string;
            mobile: string;
            type: number;
            region: {
                id: number;
                short_name: string;
            };
        }[];
        slug: string;
    };
    groups: IOfferGroupsSimplified | null;
}

export const DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE = 20;

export const OfferListPropertiesModal: React.FC<IProps> = (props) => {
    const pageChangeScrollToRef = useRef<HTMLDivElement>(null);
    const propertyList = usePropertyList(props.offer.id, PropertyListTabTypes.MODAL, pageChangeScrollToRef, DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE);
    const [currentPropertyListType, setCurrentPropertyListType] = useState(props.propertyListType ?? PropertyListType.TABLE);
    const [comparisonMode, setComparisonMode] = useState(false);

    const theme = useTheme();

    const {properties, pageCount, onPageChange: onPropertyListPageChange, onSortChange, sort, page, requestState, pageIsLoading} = propertyList;

    const onModalClose = (e: React.MouseEvent | React.KeyboardEvent) => {
        props.onModalClose && props.onModalClose(e);
        page.setCurrentPage(1);
    };

    const onChangePropertyListType = (listType: PropertyListType) => {
        setCurrentPropertyListType(listType);
        listType === PropertyListType.TILES && showPropertyPlanView(ShowPropertyPlanView.PLAN_DEFAULT, props.offer, props.offer.vendor);
    };

    const offerLink = useOfferLink(props.offer);
    const target = useResponsiveLinkTarget();

    const propertyListFilters = isEmpty(props.latestSearchFilters) ? {} : toQueryValues(propertyListTableFiltersFields, props.latestSearchFilters);

    const onPageChange = (page: number) => {
        return onPropertyListPageChange(page, propertyListFilters);
    };

    return (
        <PropertyListModal onModalClose={onModalClose} isOpen={props.modalState}>
            <div css={innerWrap}>
                {props.offer && (
                    <div css={headerStyles}>
                        {props.offer.vendor.logo && (
                            <div css={logoWrapperStyles}>
                                <img css={logoImage} alt={`${props.offer.vendor.name} logo`} src={props.offer.vendor.logo.v_log_120x90} />
                            </div>
                        )}

                        <div>
                            <Text as="h2" variant="headline_3">
                                <a href={offerLink} target={target}>
                                    {props.offer.name}
                                </a>
                            </Text>

                            {props.offer.vendor && (
                                <Text color={theme.colors.gray[700]} variant="body_copy_2" className="bt">
                                    {props.offer.vendor.name}
                                </Text>
                            )}

                            <div css={[flex("center"), mt()]}>
                                <div css={markerIconWrap}>
                                    <MapMarkerOutlineIcon size="2" />
                                </div>

                                {props.offer.type && props.offer.address && (
                                    <Text color={theme.colors.gray[700]} as="address" variant="info_txt_1">
                                        {getOfferTypeNameCapital(props.offer.type)} <span className="bt">{props.offer.address}</span>
                                    </Text>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                <>
                    {props.offer && (
                        <PropertyListTableHolder>
                            <span ref={pageChangeScrollToRef} />

                            <div css={propertyListSwitchersWrapStyle}>
                                <PropertyListTabSwitcher
                                    currentPropertyListType={currentPropertyListType}
                                    setCurrentPropertyListType={onChangePropertyListType}
                                />
                                <div css={[switcherHolder]}>
                                    <Switcher
                                        id="comparison-switcher"
                                        labelContent="Porównaj"
                                        onChange={() => {
                                            setComparisonMode(!comparisonMode);
                                        }}
                                        variant="big"
                                        checked={comparisonMode}
                                        value="comparison-mode"
                                    />
                                </div>
                            </div>

                            {currentPropertyListType === PropertyListType.TABLE && (
                                <div css={propertyListTableWrapper}>
                                    <PropertyListTable
                                        requestState={requestState}
                                        offer={props.offer}
                                        properties={properties}
                                        sort={sort.currentSort}
                                        onSortChange={(value) => onSortChange(value, propertyListFilters)}
                                        onPageChange={onPageChange}
                                        pageCount={pageCount}
                                        pageIsLoading={pageIsLoading}
                                        currentPage={page.currentPage}
                                        sourceSection={props.sourceSection}
                                        css={[modalPropertyList]}
                                        comparisonMode={comparisonMode}
                                    />
                                </div>
                            )}

                            {currentPropertyListType === PropertyListType.TILES && (
                                <PropertyListTileList
                                    requestState={requestState}
                                    properties={properties}
                                    offer={props.offer as IOfferDetail}
                                    css={[modalPropertyList]}
                                    comparisonMode={comparisonMode}
                                    gridLayoutCols={{md: 2, lg: 2}}
                                />
                            )}
                        </PropertyListTableHolder>
                    )}
                    {comparisonMode && (
                        <div css={comparisonToolLinkWrap(currentPropertyListType === PropertyListType.TILES)}>
                            <GoToComparisonToolButton />
                        </div>
                    )}

                    {pageCount > 0 && (
                        <div css={paginationHolder}>
                            <PaginationWithList
                                iconPrev={<ChevronLeftIcon />}
                                iconNext={<ChevronRightIcon />}
                                onChangePageClick={onPageChange}
                                requestState={requestState}
                                pageCount={pageCount}
                                currentPage={page.currentPage}
                                multiNumbersInside
                                size={PaginationSize.SM}
                            />
                        </div>
                    )}
                </>
            </div>
        </PropertyListModal>
    );
};

const PropertyListModal = styled(SystemModal)`
    ${fadeInAnimation("0.2s")};
`;

const innerWrap = (theme: Theme) => css`
    position: relative;
    max-height: 100vh;
    height: 100%;
    width: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    @media (min-width: ${theme.breakpoints.sm}) {
        height: 630px;
        width: 810px;
        padding: 0;
    }
`;

const PropertyListTableHolder = styled.div`
    flex-grow: 1;
    flex-shrink: 1;
    overflow-x: hidden;
    ${prettyScroll()};
`;

const modalPropertyList = css`
    margin-bottom: 0 !important;
`;

const headerStyles = (theme: Theme) => css`
    ${p(2, 0, 4, 2)};
    padding-right: 3.8rem;
    display: flex;
    flex-shrink: 0;

    @media (min-width: ${theme.breakpoints.xs}) {
        padding: 2.2rem;
    }
`;

const propertyListTableWrapper = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.md}) {
        ${ph(2)};
    }
`;

const markerIconWrap = css`
    margin-right: 1.2rem;
`;

const logoWrapperStyles = css`
    margin-right: 1.2rem;
`;

const logoImage = (theme: Theme) => css`
    max-width: 8.6rem;
    max-height: 6.4rem;

    @media (min-width: ${theme.breakpoints.xs}) {
        max-width: 11.2rem;
        max-height: 8.4rem;
    }
`;

const paginationHolder = (theme: Theme) => css`
    display: flex;
    justify-content: center;
    background: ${theme.colors.gray[100]};
    width: 100%;
    padding: 1rem;
    flex-shrink: 0;

    @media (min-width: ${theme.breakpoints.sm}) {
        position: sticky;
        bottom: 0;
        padding: 0.3rem;
    }
`;

const propertyListSwitchersWrapStyle = css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    ${ph(2)}

    ${onDesktop(css`
        flex-direction: row;
    `)}
`;

const comparisonToolLinkWrap = (inTiles: boolean) => () => css`
    ${p(1.5)}
    display: flex;
    align-items: center;
    justify-content: ${inTiles ? "center" : "flex-end"};
`;

const switcherHolder = css`
    align-self: flex-end;

    ${onDesktop(css`
        align-self: center;
    `)}
`;
